const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const getAppSessionToken = () => localStorage.getItem('app_session_token') || null;

/** 仕入先編集画面（社内の /supplier-share とは別） */
export const isSupplierEditPath = (pathname = window.location.pathname) =>
  pathname.startsWith('/supplier-share-edit');

export const isSupplierLoginPath = (pathname = window.location.pathname) =>
  pathname.startsWith('/supplier-login');

/** 仕入先トークンを送る画面（編集・ログイン） */
export const isSupplierAuthPath = (pathname = window.location.pathname) =>
  isSupplierEditPath(pathname) || isSupplierLoginPath(pathname);

export const isPublicAppPath = (pathname = window.location.pathname) =>
  pathname === '/login' || pathname.startsWith('/supplier-login');

const getSupplierAuthToken = () => {
  if (!isSupplierAuthPath()) {
    return null;
  }
  return localStorage.getItem('supplier_session_token') || null;
};

const clearSupplierSession = () => {
  localStorage.removeItem('supplier_code');
  localStorage.removeItem('supplier_session_token');
  localStorage.removeItem('supplier_name');
};

const clearAppSession = () => {
  localStorage.removeItem('app_session_token');
  localStorage.removeItem('app_user');
};

/**
 * 401 時に自動ログアウトする対象を認証系 API に限定する。
 * 別タブ共存のため、当該パスでトークンを送らない画面ではクリアしない。
 * @returns {'app'|'supplier'|null}
 */
export function classifyAuthFailureLogout(endpoint, pathname = window.location.pathname) {
  const path = endpoint.split('?')[0];
  if (path === '/api/app-auth/me') {
    // 仕入先画面では社内トークンを業務 API に付けない／me 検証も抑止する想定
    if (isSupplierAuthPath(pathname)) {
      return null;
    }
    return 'app';
  }
  if (path === '/api/supplier-auth/me') {
    // 社内画面では仕入先トークンを送らない
    if (!isSupplierAuthPath(pathname)) {
      return null;
    }
    return 'supplier';
  }
  return null;
}

export class ApiError extends Error {
  constructor(message, { status = null, detail = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function fetchAPI(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;
  const headers = {
    ...options.headers,
  };

  const endpointPath = endpoint.split('?')[0];
  const isAppAuthEndpoint = endpointPath.startsWith('/api/app-auth');

  const appToken = getAppSessionToken();
  // 編集画面の業務 API には社内トークンを付けない。
  // ただし app-auth（me 等）は別タブの社内セッション検証用に付ける。
  if (appToken && (!isSupplierEditPath() || isAppAuthEndpoint)) {
    headers['X-App-Token'] = appToken;
  }

  const supplierToken = getSupplierAuthToken();
  if (supplierToken) {
    headers['X-Supplier-Token'] = supplierToken;
  }

  const config = {
    ...options,
    headers,
  };

  if (config.body instanceof FormData) {
    // multipart
  } else if (config.body && typeof config.body === 'object') {
    headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);
    const pathname = window.location.pathname;

    if (
      response.status === 401 &&
      !isPublicAppPath(pathname) &&
      !endpoint.includes('/app-auth/login') &&
      !endpoint.includes('/supplier-auth/login')
    ) {
      const kind = classifyAuthFailureLogout(endpoint, pathname);
      if (kind === 'app') {
        clearAppSession();
        if (!pathname.startsWith('/login') && !isSupplierAuthPath(pathname)) {
          window.location.href = '/login';
        }
      } else if (kind === 'supplier') {
        clearSupplierSession();
        if (isSupplierEditPath(pathname) && !isSupplierLoginPath(pathname)) {
          window.location.href = '/supplier-login';
        }
      }
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      const detail = errorBody.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail != null
            ? JSON.stringify(detail)
            : `HTTP error! status: ${response.status}`;
      throw new ApiError(message, { status: response.status, detail });
    }

    if (response.status === 204) {
      return null;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

export const api = {
  get: (endpoint, options) => fetchAPI(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, data, options) => fetchAPI(endpoint, { ...options, method: 'POST', body: data }),
  put: (endpoint, data, options) => fetchAPI(endpoint, { ...options, method: 'PUT', body: data }),
  patch: (endpoint, data, options) => fetchAPI(endpoint, { ...options, method: 'PATCH', body: data }),
  delete: (endpoint, options) => fetchAPI(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
